import {
  AnalyticsProductLevel,
  type ListingCompDto,
  type MonthlyPriceHistoryDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatNumber } from '../../../utils/formatNumber';
import { isValidCompPrice } from '../../../utils/isValidCompPrice';
import { SpecialOfferCell } from './SpecialOfferCell';
import { Anchor, Box, Icon, Paragraph, Popover, Spinner, Tag, Text, TriggerButton } from '@zillow/constellation-10';
import { IconCheckmarkFilled, IconInfoFilled } from '@zillow/constellation-icons';

export type MarketCompsTableColumn = {
  field: keyof ListingCompDto | string;
  header: string;
  headerTooltip?: string;
  align?: 'left' | 'right';
  renderer: (value: unknown, row: ListingCompDto, isSelectedProperty?: boolean) => React.ReactNode;
  csvRenderer?: (value: unknown, row: ListingCompDto, isSelectedProperty?: boolean) => string;
  width?: string;
  cellCss?: Record<string, unknown>;
};

export const TooltipHeader = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Popover
    body={<Paragraph textStyle="body-sm">{tooltip}</Paragraph>}
    trigger={
      <Box css={{ display: 'inline-flex', alignItems: 'center', gap: '100' }}>
        <span>{label}</span>
        <TriggerButton onClick={(e) => e.stopPropagation()}>
          <Icon css={{ fill: 'icon.muted' }} size="sm">
            <IconInfoFilled />
          </Icon>
        </TriggerButton>
      </Box>
    }
    shouldAwaitInteractionResponse
    tone="neutral"
    header={<Text textStyle="body-sm-bold">{label}</Text>}
  />
);

const booleanCheckRenderer = (value: unknown) => {
  if (value !== true) return null;
  return (
    <Icon size="sm" css={{ display: 'block', margin: 'auto' }}>
      <IconCheckmarkFilled />
    </Icon>
  );
};

const booleanCheckCsvRenderer = (value: unknown) => (value === true ? 'Yes' : '');

export const makeTableConfig = (
  viewsRankMap: Map<string, number>,
  leadsRankMap: Map<string, number>,
  isOffMarket: boolean,
  priceHistoryMap: Record<string, Array<MonthlyPriceHistoryDto>> | undefined,
  isLoadingPriceHistory: boolean,
): Array<MarketCompsTableColumn> => {
  return [
    {
      field: 'propertyName',
      header: 'Property',
      renderer: (value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {row.zillowLink ? (
            <Anchor href={row.zillowLink} target="_blank" rel="noopener noreferrer">
              {String(value)}
            </Anchor>
          ) : (
            <Text textStyle="body-sm-bold">{String(value)}</Text>
          )}
          <span style={{ fontSize: '13px', color: '#666' }}>
            {row.street}, {row.city}, {row.state} {row.zip}
          </span>
        </div>
      ),
      csvRenderer: (value, row) => `${String(value)} - ${row.street}, ${row.city}, ${row.state} ${row.zip}`,
      width: '300px',
      cellCss: {
        whiteSpace: 'normal',
      },
    },
    {
      field: 'listingAlias',
      header: 'Type',
      renderer: (_value, row, isSelectedProperty) => {
        const tags: Array<{
          label: string;
          tone: 'green' | 'blue' | 'purple' | 'orange' | 'yellow' | 'teal' | 'aqua' | 'gray';
        }> = [];

        if (isSelectedProperty) {
          tags.push({ label: 'Your property', tone: 'green' });
          if (isOffMarket) {
            tags.push({ label: 'Off market', tone: 'orange' });
          }
        } else if (row.isUserOwned) {
          tags.push({ label: 'Portfolio', tone: 'blue' });
        }

        if (row.isDisabledHousing) {
          tags.push({ label: 'Disabled housing', tone: 'gray' });
        }
        if (row.isMilitaryHousing) {
          tags.push({ label: 'Military housing', tone: 'gray' });
        }
        if (row.isIncomeRestricted) {
          tags.push({ label: 'Income restricted', tone: 'gray' });
        }
        if (row.isSeniorHousing) {
          tags.push({ label: 'Senior housing', tone: 'gray' });
        }
        if (row.isStudentHousing) {
          tags.push({ label: 'Student housing', tone: 'gray' });
        }
        if (tags.length === 0) return '-';

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {tags.map((tag, index) => (
              <Tag key={index} tone={tag.tone} size="sm">
                {tag.label}
              </Tag>
            ))}
          </div>
        );
      },
      csvRenderer: (_value, row, isSelectedProperty) => {
        const tags: Array<string> = [];

        if (isSelectedProperty) {
          tags.push('Your property');
          if (isOffMarket) {
            tags.push('Off market');
          }
        } else if (row.isUserOwned) {
          tags.push('Portfolio');
        }

        if (row.isDisabledHousing) {
          tags.push('Disabled housing');
        }
        if (row.isMilitaryHousing) {
          tags.push('Military housing');
        }
        if (row.isIncomeRestricted) {
          tags.push('Income restricted');
        }
        if (row.isSeniorHousing) {
          tags.push('Senior housing');
        }
        if (row.isStudentHousing) {
          tags.push('Student housing');
        }

        return tags.length > 0 ? tags.join(', ') : '-';
      },
      width: '200px',
      cellCss: {
        whiteSpace: 'normal',
      },
    },
    {
      field: 'currentProductType',
      header: 'Advertising',
      renderer: (_value, row) => {
        if (row.isUnclaimed) return 'Unclaimed';
        const productName =
          AdproductsConstants.ProductTypes[row.currentProductType as keyof typeof AdproductsConstants.ProductTypes];
        const base = productName || (row.hasOtherProductType ? 'Other' : null);
        if (!base) return '-';
        const features = row.productFeatures?.filter(
          (f) => f !== AnalyticsProductLevel.NONE && f !== AnalyticsProductLevel.ALL,
        );
        if (features && features.length > 0) return `${base} (${features.join(', ')})`;
        return base;
      },
      csvRenderer: (_value, row) => {
        if (row.isUnclaimed) return 'Unclaimed';
        const productName =
          AdproductsConstants.ProductTypes[row.currentProductType as keyof typeof AdproductsConstants.ProductTypes];
        const base = productName || (row.hasOtherProductType ? 'Other' : null);
        if (!base) return '-';
        const features = row.productFeatures?.filter(
          (f) => f !== AnalyticsProductLevel.NONE && f !== AnalyticsProductLevel.ALL,
        );
        if (features && features.length > 0) return `${base} (${features.join(', ')})`;
        return base;
      },
      width: '160px',
    },
    {
      field: 'overlapPerc',
      header: 'View overlap',
      headerTooltip: 'The percentage of renters who viewed this property and yours.',
      align: 'right',
      renderer: (value, _row, isSelectedProperty) => {
        if (isSelectedProperty) return '-';
        const num = Number(value);
        return isNaN(num) || num === 0 ? '-' : `${num.toFixed(1)}%`;
      },
      csvRenderer: (value, _row, isSelectedProperty) => {
        if (isSelectedProperty) return '-';
        const num = Number(value);
        return isNaN(num) || num === 0 ? '-' : `${num.toFixed(1)}%`;
      },
      width: '130px',
    },
    {
      field: 'distanceMiles',
      header: 'Distance (mi)',
      align: 'right',
      renderer: (value, _row, isSelectedProperty) => (isSelectedProperty ? '-' : Number(value).toFixed(2)),
      csvRenderer: (value, _row, isSelectedProperty) => (isSelectedProperty ? '-' : Number(value).toFixed(2)),
      width: '120px',
    },
    {
      field: 'viewsRank',
      header: 'Views rank',
      headerTooltip:
        'How this property ranks by total views (last 30 days) compared to your competitor shortlist. 1 = most viewed.',
      align: 'right',
      renderer: (_value, row) => {
        const relativeRank = viewsRankMap.get(row.listingAlias);
        return relativeRank ? String(relativeRank) : '-';
      },
      csvRenderer: (_value, row) => {
        const relativeRank = viewsRankMap.get(row.listingAlias);
        return relativeRank ? String(relativeRank) : '-';
      },
      width: '110px',
    },
    {
      field: 'leadsRank',
      header: 'Leads rank',
      headerTooltip:
        'How this property ranks by leads (last 30 days) compared to your competitor shortlist. 1 = most leads.',
      align: 'right',
      renderer: (_value, row) => {
        const relativeRank = leadsRankMap.get(row.listingAlias);
        return relativeRank ? String(relativeRank) : '-';
      },
      csvRenderer: (_value, row) => {
        const relativeRank = leadsRankMap.get(row.listingAlias);
        return relativeRank ? String(relativeRank) : '-';
      },
      width: '110px',
    },
    {
      field: 'overallLowPrice',
      header: 'Low price',
      headerTooltip: 'The lowest rent this property currently advertises, across all unit types.',
      align: 'right',
      renderer: (value, row) => (value && isValidCompPrice(row) ? formatCurrency(Number(value)) : '-'),
      csvRenderer: (value, row) => (value && isValidCompPrice(row) ? String(value) : '-'),
      width: '120px',
    },
    {
      field: 'overallHighPrice',
      header: 'High price',
      headerTooltip: 'The highest rent this property currently advertises, across all unit types.',
      align: 'right',
      renderer: (value, row) => (value && isValidCompPrice(row) ? formatCurrency(Number(value)) : '-'),
      csvRenderer: (value, row) => (value && isValidCompPrice(row) ? String(value) : '-'),
      width: '120px',
    },
    {
      field: 'numUnits',
      header: 'Units',
      align: 'right',
      renderer: (value) => {
        const num = Number(value);
        return isNaN(num) ? '-' : formatNumber(num);
      },
      csvRenderer: (value) => {
        const num = Number(value);
        return isNaN(num) ? '-' : String(value);
      },
      width: '100px',
    },
    {
      field: 'yearBuilt',
      header: 'Year built',
      align: 'right',
      renderer: (value) => (value ? String(value) : '-'),
      csvRenderer: (value) => (value ? String(value) : '-'),
      width: '120px',
    },
    {
      field: 'specialOfferDescription',
      header: 'Special offer',
      renderer: (value) => <SpecialOfferCell specialOfferDescription={value as string | null | undefined} />,
      csvRenderer: (value) => (value ? String(value) : '-'),
      width: '150px',
      cellCss: {
        overflow: 'visible',
        position: 'relative',
      },
    },
    {
      field: 'airConditioning',
      header: 'A/C',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'fitnessCenter',
      header: 'Fitness',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'greenSpace',
      header: 'Green space',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'inUnitLaundry',
      header: 'In-unit laundry',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'petAmenities',
      header: 'Pet friendly',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'swimmingPool',
      header: 'Pool',
      renderer: booleanCheckRenderer,
      csvRenderer: booleanCheckCsvRenderer,
      width: '80px',
    },
    {
      field: 'parking',
      header: 'Parking',
      renderer: (_value, row) => {
        if (!row.parking || row.parking.length === 0) return '-';
        return row.parking
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1).replace(/([a-z])([A-Z])/g, '$1 $2'))
          .join(', ');
      },
      csvRenderer: (_value, row) => {
        if (!row.parking || row.parking.length === 0) return '-';
        return row.parking
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1).replace(/([a-z])([A-Z])/g, '$1 $2'))
          .join(', ');
      },
      width: '150px',
      cellCss: {
        whiteSpace: 'normal',
      },
    },
    {
      field: 'overallAveragePerSquareFoot',
      header: 'Avg $/sqft',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        return lastEntry?.overallAveragePerSquareFoot ? `$${lastEntry.overallAveragePerSquareFoot.toFixed(2)}` : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        return lastEntry?.overallAveragePerSquareFoot ? lastEntry.overallAveragePerSquareFoot.toFixed(2) : '-';
      },
      width: '110px',
    },
    {
      field: 'studio',
      header: 'Studio',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const studioPrice = lastEntry?.priceByBedroom?.[0]?.averagePrice;
        return studioPrice ? formatCurrency(studioPrice) : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const studioPrice = lastEntry?.priceByBedroom?.[0]?.averagePrice;
        return studioPrice ? studioPrice.toFixed(0) : '-';
      },
      width: '110px',
    },
    {
      field: '1br',
      header: '1BR',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[1]?.averagePrice;
        return price ? formatCurrency(price) : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[1]?.averagePrice;
        return price ? price.toFixed(0) : '-';
      },
      width: '110px',
    },
    {
      field: '2br',
      header: '2BR',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[2]?.averagePrice;
        return price ? formatCurrency(price) : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[2]?.averagePrice;
        return price ? price.toFixed(0) : '-';
      },
      width: '110px',
    },
    {
      field: '3br',
      header: '3BR',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[3]?.averagePrice;
        return price ? formatCurrency(price) : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[3]?.averagePrice;
        return price ? price.toFixed(0) : '-';
      },
      width: '110px',
    },
    {
      field: '4br',
      header: '4+BR',
      align: 'right',
      renderer: (_value, row) => {
        if (isLoadingPriceHistory) return <Spinner size="sm" />;
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[4]?.averagePrice;
        return price ? formatCurrency(price) : '-';
      },
      csvRenderer: (_value, row) => {
        const history = priceHistoryMap?.[row.listingAlias];
        const lastEntry = history?.[history.length - 1];
        const price = lastEntry?.priceByBedroom?.[4]?.averagePrice;
        return price ? price.toFixed(0) : '-';
      },
      width: '110px',
    },
  ];
};
